/**
 * CRM helpers: who may see it, how a person maps to and from the edit form, and what to tell
 * the user when the service says no.
 *
 * The CRM is for agents and brokerages only. The service enforces that on every request; this
 * file only decides what to show. REACT_APP_CRM must be 'true' at build time for any CRM UI to
 * appear, so the feature stays dark until the backend is deployed.
 */
export const CRM_ENABLED = process.env.REACT_APP_CRM === 'true';

export const CRM_ROLES = ['AGENT', 'BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN', 'BROKERAGE'];

export const canUseCrm = (role) => CRM_ROLES.includes(String(role || '').toUpperCase());

export const RELATIONSHIPS = [
  { value: 'LEAD', label: 'Lead' },
  { value: 'CLIENT', label: 'Client' },
  { value: 'PAST_CLIENT', label: 'Past client' },
  { value: 'SPHERE', label: 'Sphere of influence' },
  { value: 'VENDOR', label: 'Vendor' },
  { value: 'REFERRAL_PARTNER', label: 'Referral partner' },
];

export const SOURCES = [
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'SPHERE', label: 'Sphere' },
  { value: 'OPEN_HOUSE', label: 'Open house' },
  { value: 'WEB_FORM', label: 'Web form' },
  { value: 'ZILLOW', label: 'Zillow' },
  { value: 'REALTOR_COM', label: 'Realtor.com' },
  { value: 'SOCIAL', label: 'Social' },
  { value: 'PAID_AD', label: 'Paid ad' },
  { value: 'SIGN_CALL', label: 'Sign call' },
  { value: 'OTHER', label: 'Other' },
];

export const LEAD_TYPES = [
  { value: 'BUYER', label: 'Buyer' },
  { value: 'SELLER', label: 'Seller' },
  { value: 'BOTH', label: 'Buyer and seller' },
];

export const leadTypeLabel = (value) => LEAD_TYPES.find((x) => x.value === value)?.label || '';

/** A seller (or both) has a property to sell; a buyer (or both) has areas of interest. */
export const sellsProperty = (type) => type === 'SELLER' || type === 'BOTH';
export const buysProperty = (type) => type === 'BUYER' || type === 'BOTH';

const labelOf = (list, value) => list.find((x) => x.value === value)?.label || value || '';
export const relationshipLabel = (value) => labelOf(RELATIONSHIPS, value);
export const sourceLabel = (value) => labelOf(SOURCES, value);

export const bookLabel = (book) => (book === 'brokerage' ? 'Brokerage book' : 'My book');

/**
 * Which books the caller can open, from GET /api/v1/crm/books.
 * Returns e.g. [{ book: 'personal' }, { book: 'brokerage', owners: ['b1'] }].
 */
export const availableBooks = (books) => {
  if (!books) return [];
  const out = [];
  if (books.hasPersonalBook) out.push({ book: 'personal', owners: [] });
  const owners = books.brokerageBookOwnerUserIds || [];
  if (owners.length > 0) out.push({ book: 'brokerage', owners });
  return out;
};

export const personDisplayName = (p) => {
  if (!p) return '';
  if (p.decryptionFailed) return 'Details unavailable';
  const name = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
  if (name) return name;
  return primaryValue(p.emails) || primaryValue(p.phones) || 'Unnamed';
};

export const primaryValue = (list) => {
  if (!Array.isArray(list) || list.length === 0) return '';
  return (list.find((x) => x.primary) || list[0]).value || '';
};

export const parseTags = (text) => {
  const seen = new Set();
  const out = [];
  String(text || '')
    .split(/[,\n]/)
    .map((t) => t.trim())
    .filter(Boolean)
    .forEach((t) => {
      const key = t.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(t);
      }
    });
  return out;
};

export const emptyAddress = () => ({ line1: '', line2: '', city: '', state: '', zip: '' });

/** "12 Oak St, Unit 4, Oakland, CA 94601" from whatever parts are present. */
export const formatAddress = (a) => {
  if (!a) return '';
  const cityState = [a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [a.line1, a.line2, cityState].filter(Boolean).join(', ');
};

/**
 * What to show in the "Property / areas" column: a seller's property, a buyer's areas, or both
 * (labelled) for someone who is doing both.
 */
export const propertyOrAreasSummary = (p) => {
  if (!p || p.decryptionFailed) return '';
  const property = formatAddress(p.propertyAddress);
  const areas = (p.areasOfInterest || []).join(', ');
  if (p.leadType === 'BOTH') {
    return [property && `Selling: ${property}`, areas && `Looking in: ${areas}`].filter(Boolean).join(' · ');
  }
  if (p.leadType === 'SELLER') return property;
  if (p.leadType === 'BUYER') return areas;
  return '';
};

export const emptyPersonForm = () => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  tags: '',
  notes: '',
  relationship: 'LEAD',
  source: 'OTHER',
  leadType: '',
  propertyAddress: emptyAddress(),
  areas: [],
  // Extra emails and phones the form has no field for are carried through, so editing a person
  // never silently drops them.
  otherEmails: [],
  otherPhones: [],
});

export const personToForm = (p) => {
  const primaryEmail = (p.emails || []).find((x) => x.primary) || (p.emails || [])[0];
  const primaryPhone = (p.phones || []).find((x) => x.primary) || (p.phones || [])[0];
  return {
    firstName: p.firstName || '',
    lastName: p.lastName || '',
    email: primaryEmail?.value || '',
    phone: primaryPhone?.value || '',
    tags: (p.tags || []).join(', '),
    notes: p.notes || '',
    relationship: p.relationship || 'LEAD',
    source: p.source || 'OTHER',
    leadType: p.leadType || '',
    propertyAddress: { ...emptyAddress(), ...(p.propertyAddress || {}) },
    areas: [...(p.areasOfInterest || [])],
    otherEmails: (p.emails || []).filter((x) => x !== primaryEmail),
    otherPhones: (p.phones || []).filter((x) => x !== primaryPhone),
  };
};

/** Builds the request body. `isUpdate` leaves out `source`, which can only be set when creating. */
export const formToRequest = (form, isUpdate = false) => {
  const emails = [];
  if (form.email.trim()) emails.push({ value: form.email.trim(), primary: true });
  (form.otherEmails || []).forEach((e) => emails.push({ value: e.value, label: e.label, primary: false }));
  const phones = [];
  if (form.phone.trim()) phones.push({ value: form.phone.trim(), primary: true });
  (form.otherPhones || []).forEach((p) => phones.push({ value: p.value, label: p.label, primary: false }));

  const body = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    emails,
    phones,
    tags: parseTags(form.tags),
    notes: form.notes.trim(),
    relationship: form.relationship,
  };
  if (!isUpdate) body.source = form.source;

  // Buyer or seller. When creating, an unset type is simply left out; when editing, "not set" is
  // sent as NONE so a type that was set can be cleared.
  if (form.leadType) body.leadType = form.leadType;
  else if (isUpdate) body.leadType = 'NONE';
  // Only what applies to the chosen type is sent (the service drops the rest anyway).
  if (sellsProperty(form.leadType)) body.propertyAddress = { ...emptyAddress(), ...form.propertyAddress };
  if (buysProperty(form.leadType)) body.areasOfInterest = [...(form.areas || [])];
  return body;
};

/** Problems to fix before saving, or '' when the form is fine. */
export const validatePersonForm = (form) => {
  if (!form.firstName.trim() && !form.lastName.trim() && !form.email.trim() && !form.phone.trim()) {
    return 'Enter at least a name, an email or a phone number.';
  }
  if (form.relationship === 'LEAD' && !form.leadType) {
    return 'Choose whether this lead is a buyer or a seller.';
  }
  return '';
};

/** What to tell the user for a failed CRM call. Never echoes anything the user typed. */
export const crmErrorMessage = (err) => {
  const status = err?.response?.status;
  if (status === 403) return 'Connect is available to agents and brokerages only. Check the role you are acting as.';
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 404) return 'That person could not be found. They may have been removed.';
  if (status === 409) return 'Someone in this book already has that email address or phone number.';
  if (status === 400) return err?.response?.data?.message || 'Please check the details and try again.';
  if (status === 503) return 'Connect is temporarily unavailable. Please try again in a moment.';
  return 'Something went wrong. Please try again.';
};
